define((require) => {
    "use strict";

    const _                     = require("underscore");
    const Matrix                = require("shared/vendor/Matrix");
    const Control               = require("ui/experience/widgets/transformations/Control");
    const TGroup                = require("ui/experience/widgets/transformations/TGroup");
    const WidgetTypes           = require("shared/ui/experience/widgets/WidgetTypes");
    const EditorActivityManager = require("editor/ui/experience/editor/activity/EditorActivityManager");
    const UUIDGenerator         = require("ui/experience/widgets/UUIDGenerator");
    /**
     * Controls class manages all the controls for transformable elements
     * Controls is a collection of {@link Control}
     *
     * @param container
     * @constructor
     */
    function Controls(container) {
        this.container = container;
        this.target = null;
        this.scale  = 1;
        this.isMoving = false;

        // transform interaction
        // where interaction starts
        this.startX = 0;
        this.startY = 0;

        // transforms to apply
        this.preMatrix = new Matrix();
        this.postMatrix = new Matrix();

        // where interaction ends
        this.endX = 0;
        this.endY = 0;

        // transform UV delta
        this.dU = 0;
        this.dV = 0;

        this.regU = 0.5;
        this.regV = 0.5;
        // registration point in px
        this.regX = 0;
        this.regY = 0;

        // inverted matrices
        this.inv = new Matrix();

        // transform controls
        this.control = null;
        this.controls = Controls.defaults();

        this.draw();
        // hide initially
        this.hide();
    }

    Controls.defaults = () => {
        return [
            new Control(Control.BORDER),
            new Control(Control.TARGET),
            new Control(Control.EDGE_TRANSLATE, 0, 0, -10, -10, null),
            new Control(Control.SCALE_X, 0, 0.5, 0, 0, "w"),
            new Control(Control.ROTATE_SCALE, 0, 0.5, 0, 0, null),
            new Control(Control.SCALE_X, 1, 0.5, 0, 0, "e"),
            new Control(Control.ROTATE_SCALE, 1, 0.5, 0, 0, null),
            new Control(Control.SCALE_Y, 0.5, 0, 0, 0, "n"),
            new Control(Control.SCALE_Y, 0.5, 1, 0, 0, "s"),
            new Control(Control.SCALE_UNIFORM, 0, 0, 0, 0, "nw"),
            new Control(Control.SCALE_UNIFORM, 0, 1, 0, 0, "sw"),
            new Control(Control.SCALE_UNIFORM, 1, 0, 0, 0, "ne"),
            new Control(Control.SCALE_UNIFORM, 1, 1, 0, 0,  "se"),
            new Control(Control.ROTATE, 0.5, 1, 0, 15, null)
        ];
    };

    Controls.prototype = {

        setTarget(target) {
            if (target) {
                this.target = target;
                this.updateFromTarget();
            }
        },

        removeTarget() {
            this.target = null;
            this.hide();
        },

        targetIs(t) {
            return !!this.target && this.target === t;
        },

        hasTarget() {
            return !!this.target;
        },

        /**
         * Update the target when mouse clicks, this handles multiple-selection mode also.
         * @param {TElement} transformableElement
         */
        updateTarget(transformableElement, modifier) {
            if (!transformableElement) {
                this.removeTarget();
                return;
            }

            if (transformableElement === this.target) {
                return;
            }

            let newTarget = this.target; // default to the current one
            const isTGroup  = this.target instanceof TGroup;

            if (modifier && this.target) {
                newTarget = isTGroup ? this.target : new TGroup([this.target]);

                if (newTarget.has(transformableElement)) {
                    newTarget.remove(transformableElement);
                } else {
                    newTarget.add(transformableElement);
                }

                // covert back to a single element
                if (newTarget.members.length === 1) {
                    newTarget = this.target.members[0];
                }
            } else if (!(isTGroup && this.target.has(transformableElement))) {
                newTarget = transformableElement;
            }

            this.setTarget(newTarget);
        },

        updateFromTarget(scale) {
            this.scale = scale || this._getContainerScale();

            if (this.target) {
                this.target.updateFromTarget();
                this.update();
                this.config(this.target.getControlsConf());
            }
        },

        _getContainerScale() {
            return Matrix.createFromDom(document.getElementById("experiences-region")).getScaleX();
        },

        /**
         * Show or hide each control in the control set according to the configuration
         *
         * @param configure
         */
        config(configure) {
            this.controls.forEach((control) => {
                control.setDisplay(configure[control.type] === false ? "none" : "");
            });
        },

        findControlByType(type) {
            return _.find(this.controls, (control) => control.type === type);
        },

        getControlAt(x, y) {
            // walking in reverse order to find those
            // drawn on top (later in list) first
            let i = this.controls.length;
            while (i--) {
                if (this.controls[i].contains(x, y, this.target)) {
                    return this.controls[i];
                }
            }

            // control not found
            return null;
        },

        /**
         * Find the counter control as fixed point when scaling
         */
        _findCounterControl(control) {
            if (!control || control.type === Control.ROTATE) return null;

            const counterU = Math.abs(control.u - 1);
            const counterV = Math.abs(control.v - 1);

            return _.find(this.controls, (ctr) => ctr.u === counterU && ctr.v === counterV);
        },

        draw() {
            _.invoke(this.controls, "draw", this.container);
        },

        /**
         *  Update the current controls to reflect the target transformation
         */
        update() {
            // Moving widgets (dragging/rotating/scaling) require immediate updates to appear responsive.
            if (this.isMoving) {
                this.onUpdate();
            } else {
                _.defer(() => {
                    this.onUpdate();
                });
            }
        },
        onUpdate() {
            const target = this.target;
            if (!target) {
                return;
            }
            const matrix = target.getMatrixWithParentOffset();

            matrix.x *= this.scale;
            matrix.y *= this.scale;
            this.controls.forEach((control) => {
                control.updatePosition(matrix, target.getWidth() * this.scale, target.getHeight() * this.scale);
            });
        },

        /**
         * Prepare the initial state before transformation
         *
         * @param x  coordinate x when the mouse is down
         * @param y  coordinate y when the mouse is down
         * @param control  used as default control when no control found
         */
        start(x, y) {
            this.commit();
            this.control = this.getControlAt(x, y) || this.findControlByType(Control.TARGET);

            this.startX = x;
            this.startY = y;
            this.dU = 0;
            this.dV = 0;
            this.setLocalReg();
            this.setGlobalReg();
        },

        move(x, y) {
            // Need to track, if the active widget is image
            // And if the cropping are active
            // To prevent dragging conflicts
            if (this.target.data && WidgetTypes.lookup(this.target.data.widget.model.get("type")).id === "image") {
                if (this.target.data.widget.model.get("isDragAvailable") === false) {
                    return;
                }
            }

            if (!this.isMoving) {
                this.isMoving = true;
                this.target.setAttr("data-transforming", true);
                this.addClass("transforming");
                if (this.target.data && this.target.data.widget) {
                    this.target.data.widget.model.set("isTransforming", true);
                }

                if (this.target.members) {
                    EditorActivityManager.setBatchActivityId(UUIDGenerator.createUUID());
                }
            }
            this.computeMoveValues(x, y);
            if (this.control) {
                this.computeTransformOffset();
                this.target.transform(this.preMatrix, this.postMatrix, this.regU, this.regV);
            }
            // use requestAnimationFrame will improve the performance
            window.requestAnimationFrame(() => {
                if (this.target && this.target.draw) {
                    this.target.draw();
                }
                this.update();
            });
        },

        end() {
            if (this.target.members && this.isMoving) {
                EditorActivityManager.setBatchActivityId(null);
            }
            this.target.setAttr("data-transforming", "");
            this.removeClass("transforming");
            this.isMoving = false;
            if (this.target.data && this.target.data.widget) {
                this.target.data.widget.model.set("isTransforming", false);
            }
            this.commit();
            this.update();
            this.control = null;
        },

        computeMoveValues(x, y) {
            this.endX = x;
            this.endY = y;

            this.cx = (this.endX - this.startX) / this.scale;
            this.cy = (this.endY - this.startY) / this.scale;

            // inline transformPoint to target local space
            this.dU = (this.inv.a * this.cx + this.inv.c * this.cy) / this.target.width;
            this.dV = (this.inv.d * this.cy + this.inv.b * this.cx) / this.target.height;
        },

        /**
         * Compute the transformation, the result is the variation to the starting point
         *
         * Note: the transformation natively happens in this order: scale first, then translate or rotate,
         * the order affects the result, this is why we need preMatrix and postMatrix variables.
         */
        computeTransformOffset() {
            let w = this.target.width;
            let h = this.target.height;
            let x = 0;
            let y = 0;

            // difference between registration and control points
            let cu = this.control.u - this.regU;
            let cv = this.control.v - this.regV;

            this.preMatrix.identity();
            this.postMatrix.identity();

            // if the abs px difference is less than 0, normalize to
            // 1 (or -1) to prevent outrageous divisions by 0 or a
            // very small number resulting in oversized transforms
            if (cu > 0) {
                if (cu * w < 1) {
                    cu = 1 / w;
                }
            } else if (cu * w > -1) {
                cu = -1 / w;
            }

            if (cv > 0) {
                if (cv * h < 1) {
                    cv = 1 / h;
                }
            } else if (cv * h > -1) {
                cv = -1 / h;
            }

            // perform transform based on control type
            switch (this.control.type) {
                case Control.SCALE: {
                    x = (cu + this.dU) / cu;
                    y = (cv + this.dV) / cv;
                    this.preMatrix.scale(x, y);
                    break;
                }

                case Control.SCALE_X: {
                    x = (cu + this.dU) / cu;
                    this.preMatrix.scale(x, 1);
                    break;
                }

                case Control.SCALE_Y: {
                    y = (cv + this.dV) / cv;
                    this.preMatrix.scale(1, y);
                    break;
                }

                case Control.SCALE_UNIFORM: {
                    x = (cu + this.dU) / cu;
                    y = (cv + this.dV) / cv;

                    // find the ratio to make the scaling
                    // uniform in both the x (w) and y (h) axes
                    w = y ? Math.abs(x / y) : 0;
                    h = x ? Math.abs(y / x) : 0;

                    // for 0 scale, scale both axises to 0
                    if (w === 0 || h === 0) {
                        x = 0;
                        y = 0;

                    // scale mased on the smaller ratio
                    } else if (w > h) {
                        x *= h;
                    } else {
                        y *= w;
                    }

                    this.preMatrix.scale(x, y);
                    break;
                }

                case Control.ROTATE_SCALE: {
                    // rotation in global space
                    x = this.startX - this.regX;
                    y = this.startY - this.regY;
                    const ex = this.endX - this.regX;
                    const ey = this.endY - this.regY;

                    const angle = Math.atan2(ey, ex) - Math.atan2(y, x);
                    this.postMatrix.rotate(angle);

                    // determine scale factor from change
                    // this is also done in global space
                    // in matching with the rotation
                    let s = Math.sqrt(x * x + y * y);
                    if (s === 0) {
                        this.preMatrix.scale(0, 0);
                    } else {
                        s = Math.sqrt(ex * ex + ey * ey) / s;
                        this.preMatrix.scale(s, 1);
                    }
                    break;
                }

                case Control.ROTATE: {
                    // rotation in global space
                    x = Math.atan2(this.startY - this.regY, this.startX - this.regX);
                    y = Math.atan2(this.endY - this.regY, this.endX - this.regX);
                    this.postMatrix.rotate(y - x);
                    break;
                }

                case Control.EDGE_TRANSLATE:
                case Control.TARGET: {
                    // translate in global space
                    this.postMatrix.translate(this.cx, this.cy);
                    break;
                }

                default:
                    break;
            }
        },


        // commit the end state when transformation ends
        commit() {
            this.target.commit();

            // update inversion matrix
            this.inv.copyFrom(this.target.startMatrix);
            this.inv.invert();
        },

        /**
         * The registration(pivot) point in local space for scaling and rotating
         */
        setLocalReg() {
            const counterControl = this._findCounterControl(this.control);

            if (counterControl) {
                this.regU = counterControl.u;
                this.regV = counterControl.v;
            } else {
                this.regU = 0.5;
                this.regV = 0.5;
            }
        },

        /**
         * The registration(pivot) point in global space for scaling and rotating
         */
        setGlobalReg() {
            const x = this.regU * this.target.width * this.scale;
            const y = this.regV * this.target.height * this.scale;
            let m = this.target.getMatrix();

            m.x *= this.scale;
            m.y *= this.scale;
            m = m.getGlobalCoordByLocalCoord(x, y);

            this.regX = m.x;
            this.regY = m.y;
        },

        /**
         * Show all controls, we need to respect the control configuration here
         */
        show() {
            this.config(this.target.getControlsConf());
        },

        /**
         * Hide all controls
         */
        hide() {
            this.controls.forEach((control) => {
                control.setDisplay("none");
            });
        },

        setControlClass(value) {
            this.controls.forEach((control) => {
                control.setClass(value);
            });
        },

        removeControlClass(value) {
            this.controls.forEach((control) => {
                control.removeClass(value);
            });
        },

        addClass(className) {
            if (this.container.className.indexOf(className) === -1) {
                this.container.className = `${className} ${this.container.className}`;
            }
        },

        removeClass(className) {
            this.container.className = this.container.className.replace(className, "");
        },

        isTransformed() {
            return this.target.isTransformed();
        },

        translate(x, y) {
            this.target.translate(x, y);
            this.update();
        }
    };

    return Controls;
});



// WEBPACK FOOTER //
// ./src/editor/ui/experience/widgets/transformations/Controls.js